import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import axios from 'axios'
import { Loader2, LogOut, Plus, X, CheckCircle, AlertCircle } from 'lucide-react'
import AddressAutocomplete from '@/components/address/AddressAutocomplete'
import WheelDateTimeField from './WheelDateTimeField'
import StopsEditor, { Stop, toStopPayload } from './StopsEditor'

// 個人會員頁面：LINE 登入 → 下單 / 查詢 / 修改 / 取消。與公司客戶原有的 /booking 流程完全分開
const api = axios.create({ baseURL: import.meta.env.VITE_API_BASE_URL || '/api/v1', timeout: 30000 })

const LIFF_SDK_URL = 'https://static.line-scdn.net/liff/edge/2/sdk.js'
const AIRPORTS = ['桃園機場一航廈', '桃園機場二航廈', '松山機場', '台中機場', '高雄機場']
const CATEGORIES = [
  { value: '送機', label: '送機 (出國)' },
  { value: '接機', label: '接機 (回國)' },
  { value: '交通趟', label: '交通趟 (2地接送)' },
]

interface MemberInfo { tenant_name: string; vehicle_categories: string[]; edit_hours: number; liff_id: string; dev_login: boolean }
interface MemberOrder {
  order_number: string; order_category: string; status: string; pickup_datetime: string | null
  pickup_location: string; dropoff_location: string; passenger_name: string; passenger_phone: string
  passenger_count: number; flight_number?: string; vehicle_type?: string; driver_notes?: string
  total_amount: number; driver_name?: string; driver_phone?: string; vehicle_number?: string
  can_edit: boolean; edit_deadline: string | null
  additional_stops?: { address: string; order: number; name?: string; phone?: string }[] | null
}

const inputCls = 'w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#969737]/50 bg-white'
const btnPrimary = 'px-4 py-2.5 rounded-lg bg-[#7f802e] text-white text-sm font-medium hover:bg-[#6b6c26] disabled:opacity-50 cursor-pointer transition-colors'
function TabRow<T extends string>({ options, value, onChange }: { options: { value: T; label: string }[]; value: T; onChange: (v: T) => void }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map(o => (
        <button key={o.value} type="button" onClick={() => onChange(o.value)}
          className={`flex-1 min-w-[30%] py-2 rounded-lg text-sm cursor-pointer transition-colors ${value === o.value ? 'bg-[#7f802e] text-white font-medium' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'}`}>{o.label}</button>
      ))}
    </div>
  )
}

const btnGhost = 'px-4 py-2.5 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer transition-colors'

const STATUS_COLOR: Record<string, string> = {
  待派車: 'bg-amber-100 text-amber-700', 已派車: 'bg-blue-100 text-blue-700',
  進行中: 'bg-indigo-100 text-indigo-700', 已完成: 'bg-green-100 text-green-700', 已取消: 'bg-gray-200 text-gray-600',
}

const errMsg = (e: any, fallback: string) => e?.response?.data?.detail || fallback

const splitDateTime = (iso: string | null) => {
  if (!iso) return { date: '', time: '' }
  const [d, t] = iso.split('T')
  return { date: d, time: (t || '').slice(0, 5) }
}

const loadLiff = (): Promise<any> =>
  new Promise((resolve, reject) => {
    if ((window as any).liff) return resolve((window as any).liff)
    const s = document.createElement('script')
    s.src = LIFF_SDK_URL
    s.onload = () => resolve((window as any).liff)
    s.onerror = () => reject(new Error('LINE 載入失敗'))
    document.head.appendChild(s)
  })

export default function MemberPage() {
  const { token = '' } = useParams()
  const [search] = useSearchParams()
  const sessionKey = `member_session_${token}`
  const [info, setInfo] = useState<MemberInfo | null>(null)
  const [fatal, setFatal] = useState('')
  const [session, setSession] = useState<string>(() => { try { return localStorage.getItem(sessionKey) || '' } catch { return '' } })
  const [member, setMember] = useState<{ name: string; phone: string } | null>(null)
  const [idToken, setIdToken] = useState('')
  const [needRegister, setNeedRegister] = useState(false)
  const [busy, setBusy] = useState(true)
  const [tab, setTab] = useState<'orders' | 'new'>('orders')
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null)

  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${session}` } }), [session])

  const saveSession = (s: string) => {
    setSession(s)
    try { s ? localStorage.setItem(sessionKey, s) : localStorage.removeItem(sessionKey) } catch { /* ignore */ }
  }
  const logout = () => { saveSession(''); setMember(null); setNeedRegister(false); setIdToken('') }

  // 登入：先試已存的 session，否則走 LINE（或測試登入）
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const { data } = await api.get<MemberInfo>(`/member/${token}/info`)
        if (cancelled) return
        setInfo(data)
        if (session) {
          try {
            const me = await api.get('/member/me', auth)
            if (!cancelled) { setMember(me.data); setBusy(false) }
            return
          } catch { saveSession('') }
        }
        let idt = ''
        const dev = search.get('dev')
        if (data.dev_login && dev) {
          idt = `dev:${dev}`
        } else {
          if (!data.liff_id) throw new Error('LINE 登入尚未設定')
          const liff = await loadLiff()
          await liff.init({ liffId: data.liff_id })
          if (!liff.isLoggedIn()) { liff.login({ redirectUri: window.location.href }); return }
          idt = liff.getIDToken()
        }
        const res = await api.post(`/member/${token}/login`, { id_token: idt })
        if (cancelled) return
        if (res.data.registered) { saveSession(res.data.session); setMember(res.data.member) }
        else { setIdToken(idt); setNeedRegister(true) }
        setBusy(false)
      } catch (e: any) {
        if (!cancelled) { setFatal(errMsg(e, e?.message || '無法載入，請稍後再試')); setBusy(false) }
      }
    })()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  if (fatal) return <Shell title="會員"><p className="text-sm text-red-600 flex items-center gap-2"><AlertCircle size={16} />{fatal}</p></Shell>
  if (busy || !info) return <Shell title="會員"><div className="flex justify-center py-10"><Loader2 className="animate-spin text-[#7f802e]" /></div></Shell>

  if (needRegister) {
    return (
      <Shell title={info.tenant_name}>
        <RegisterForm onDone={(s, m) => { saveSession(s); setMember(m); setNeedRegister(false) }} token={token} idToken={idToken} />
      </Shell>
    )
  }
  if (!member) return <Shell title={info.tenant_name}><p className="text-sm text-gray-600">登入中…</p></Shell>

  return (
    <Shell title={info.tenant_name} right={
      <button onClick={logout} className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 cursor-pointer"><LogOut size={14} />登出</button>}>
      <p className="text-sm text-gray-600 mb-3">{member.name}，您好</p>
      <div className="flex gap-2 mb-4">
        <button onClick={() => setTab('orders')} className={`flex-1 py-2 rounded-lg text-sm font-medium cursor-pointer ${tab === 'orders' ? 'bg-[#7f802e] text-white' : 'bg-white border border-gray-300 text-gray-700'}`}>我的訂單</button>
        <button onClick={() => setTab('new')} className={`flex-1 py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-1 cursor-pointer ${tab === 'new' ? 'bg-[#7f802e] text-white' : 'bg-white border border-gray-300 text-gray-700'}`}><Plus size={14} />新增訂單</button>
      </div>
      {notice && (
        <div className={`mb-3 rounded-lg px-3 py-2 text-sm flex items-center gap-2 ${notice.ok ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
          {notice.ok ? <CheckCircle size={16} /> : <AlertCircle size={16} />}{notice.text}
        </div>
      )}
      {tab === 'orders'
        ? <OrdersTab auth={auth} editHours={info.edit_hours} onNotice={setNotice} onUnauthorized={logout} />
        : <NewOrderTab auth={auth} info={info} member={member} onNotice={setNotice} onCreated={() => setTab('orders')} onUnauthorized={logout} />}
    </Shell>
  )
}

function Shell({ title, right, children }: { title: string; right?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-[#f5f7fa]">
      <div className="max-w-xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-lg font-bold text-gray-900">{title}</h1>{right}
        </div>
        {children}
      </div>
    </div>
  )
}

function RegisterForm({ token, idToken, onDone }: { token: string; idToken: string; onDone: (s: string, m: { name: string; phone: string }) => void }) {
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [err, setErr] = useState('')
  const [saving, setSaving] = useState(false)
  const submit = async () => {
    setErr('')
    if (!name.trim() || !phone.trim()) { setErr('請輸入姓名與手機號碼'); return }
    setSaving(true)
    try {
      const { data } = await api.post(`/member/${token}/register`, { id_token: idToken, name, phone })
      onDone(data.session, data.member)
    } catch (e) { setErr(errMsg(e, '註冊失敗，請稍後再試')) } finally { setSaving(false) }
  }
  return (
    <div className="bg-white rounded-xl p-4 space-y-3">
      <p className="text-sm text-gray-700">第一次使用，請留下聯絡資料</p>
      <input className={inputCls} placeholder="姓名" value={name} onChange={e => setName(e.target.value)} />
      <input className={inputCls} placeholder="手機號碼" inputMode="tel" value={phone} onChange={e => setPhone(e.target.value)} />
      {err && <p className="text-xs text-red-600">{err}</p>}
      <button className={`${btnPrimary} w-full`} disabled={saving} onClick={submit}>{saving ? '送出中…' : '完成註冊'}</button>
    </div>
  )
}

type Auth = { headers: { Authorization: string } }
type Notice = (n: { ok: boolean; text: string } | null) => void

function OrdersTab({ auth, editHours, onNotice, onUnauthorized }: { auth: Auth; editHours: number; onNotice: Notice; onUnauthorized: () => void }) {
  const [orders, setOrders] = useState<MemberOrder[] | null>(null)
  const [editing, setEditing] = useState<MemberOrder | null>(null)

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/member/me/orders', auth)
      setOrders(data.orders)
    } catch (e: any) {
      if (e?.response?.status === 401) onUnauthorized()
      else onNotice({ ok: false, text: errMsg(e, '載入訂單失敗') })
    }
  }, [auth, onNotice, onUnauthorized])
  useEffect(() => { load() }, [load])

  const cancel = async (o: MemberOrder) => {
    if (!window.confirm(`確定要取消 ${o.pickup_datetime?.replace('T', ' ').slice(0, 16)} 的訂單嗎？`)) return
    try {
      await api.post(`/member/me/orders/${o.order_number}/cancel`, {}, auth)
      onNotice({ ok: true, text: '訂單已取消' }); load()
    } catch (e) { onNotice({ ok: false, text: errMsg(e, '取消失敗') }) }
  }

  if (!orders) return <div className="flex justify-center py-10"><Loader2 className="animate-spin text-[#7f802e]" /></div>
  if (orders.length === 0) return <p className="text-sm text-gray-500 text-center py-10">還沒有訂單</p>

  return (
    <div className="space-y-3">
      {orders.map(o => (
        <div key={o.order_number} className="bg-white rounded-xl p-4 space-y-1.5">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-gray-900">{o.order_category}　{o.pickup_datetime?.replace('T', ' ').slice(0, 16)}</span>
            <span className={`text-xs px-2 py-0.5 rounded ${STATUS_COLOR[o.status] || 'bg-gray-100 text-gray-600'}`}>{o.status}</span>
          </div>
          <div className="text-sm text-gray-700">{o.pickup_location} → {o.dropoff_location}</div>
          <div className="text-xs text-gray-500">{o.passenger_name}　{o.passenger_count} 人{o.flight_number ? `　航班 ${o.flight_number}` : ''}{o.vehicle_type ? `　${o.vehicle_type}` : ''}</div>
          {o.total_amount > 0 && <div className="text-xs text-gray-500">車資 ${o.total_amount.toLocaleString()}</div>}
          {o.driver_name && <div className="text-xs text-gray-500">司機 {o.driver_name}　{o.vehicle_number || ''}　{o.driver_phone || ''}</div>}
          {o.can_edit ? (
            <div className="flex gap-2 pt-2">
              <button className={btnGhost} onClick={() => setEditing(o)}>修改</button>
              <button className="px-4 py-2.5 rounded-lg border border-red-300 text-sm text-red-600 hover:bg-red-50 cursor-pointer" onClick={() => cancel(o)}>取消訂單</button>
            </div>
          ) : (o.status === '待派車' || o.status === '已派車') && (
            <p className="text-xs text-gray-400 pt-1">上車前 {editHours} 小時內如需變更，請聯絡車行</p>
          )}
        </div>
      ))}
      {editing && <EditModal auth={auth} order={editing} onClose={() => setEditing(null)} onNotice={onNotice} onSaved={() => { setEditing(null); load() }} />}
    </div>
  )
}

function EditModal({ auth, order, onClose, onSaved, onNotice }: { auth: Auth; order: MemberOrder; onClose: () => void; onSaved: () => void; onNotice: Notice }) {
  const init = splitDateTime(order.pickup_datetime)
  const [date, setDate] = useState(init.date)
  const [time, setTime] = useState(init.time)
  const [pickup, setPickup] = useState(order.pickup_location)
  const [dropoff, setDropoff] = useState(order.dropoff_location)
  const [stops, setStops] = useState<Stop[]>(() =>
    [...(order.additional_stops || [])].sort((a, b) => a.order - b.order).map(s => ({ address: s.address, name: s.name || '', phone: s.phone || '' })))
  const [name, setName] = useState(order.passenger_name || '')
  const [phone, setPhone] = useState(order.passenger_phone || '')
  const [count, setCount] = useState(order.passenger_count || 1)
  const [flight, setFlight] = useState(order.flight_number || '')
  const [notes, setNotes] = useState(order.driver_notes || '')
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState('')

  const save = async () => {
    setSaving(true); setErr('')
    try {
      await api.patch(`/member/me/orders/${order.order_number}`, {
        pickup_datetime: `${date}T${time}:00`, pickup_location: pickup, dropoff_location: dropoff,
        passenger_name: name, passenger_phone: phone, passenger_count: count, flight_number: flight, driver_notes: notes,
        ...(stops.length || order.additional_stops?.length ? { additional_stops: toStopPayload(stops) } : {}),
      }, auth)
      onNotice({ ok: true, text: '訂單已修改' }); onSaved()
    } catch (e) { setErr(errMsg(e, '修改失敗')) } finally { setSaving(false) }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center">
      <div className="bg-white w-full max-w-xl rounded-t-2xl sm:rounded-2xl p-4 max-h-[90vh] overflow-y-auto space-y-3">
        <div className="flex items-center justify-between"><h2 className="font-bold text-gray-900">修改訂單</h2><button onClick={onClose} className="cursor-pointer"><X size={18} /></button></div>
        <WheelDateTimeField date={date} time={time} onChange={(d, t) => { setDate(d); setTime(t) }} />
        <AddressAutocomplete value={pickup} onChange={setPickup} placeholder="上車地點" />
        <StopsEditor stops={stops} onChange={setStops} />
        <AddressAutocomplete value={dropoff} onChange={setDropoff} placeholder="下車地點" />
        <div className="grid grid-cols-2 gap-2">
          <input className={inputCls} placeholder="乘客姓名" value={name} onChange={e => setName(e.target.value)} />
          <input className={inputCls} placeholder="聯絡電話" inputMode="tel" value={phone} onChange={e => setPhone(e.target.value)} />
          <input className={inputCls} type="number" min={1} placeholder="人數" value={count} onChange={e => setCount(parseInt(e.target.value) || 1)} />
          <input className={inputCls} placeholder="航班編號" value={flight} onChange={e => setFlight(e.target.value)} />
        </div>
        <textarea className={inputCls} rows={2} placeholder="備註" value={notes} onChange={e => setNotes(e.target.value)} />
        <p className="text-xs text-gray-400">若訂單已派車，修改後會重新安排司機。</p>
        {err && <p className="text-xs text-red-600">{err}</p>}
        <div className="flex gap-2"><button className={btnGhost} onClick={onClose}>關閉</button><button className={`${btnPrimary} flex-1`} disabled={saving} onClick={save}>{saving ? '儲存中…' : '儲存修改'}</button></div>
      </div>
    </div>
  )
}

function NewOrderTab({ auth, info, member, onNotice, onCreated, onUnauthorized }: {
  auth: Auth; info: MemberInfo; member: { name: string; phone: string }; onNotice: Notice; onCreated: () => void; onUnauthorized: () => void
}) {
  const [category, setCategory] = useState('送機')
  const isTrip = category === '交通趟'
  const [airport, setAirport] = useState(AIRPORTS[0])
  const [regions, setRegions] = useState<string[]>([])
  const [region, setRegion] = useState('')
  const [vehicle, setVehicle] = useState(info.vehicle_categories[0] || '')
  const [date, setDate] = useState('')
  const [time, setTime] = useState('')
  const [address, setAddress] = useState('')
  const [tripFrom, setTripFrom] = useState('')
  const [tripTo, setTripTo] = useState('')
  const [flight, setFlight] = useState('')
  const [flightTime, setFlightTime] = useState('')
  const [stops, setStops] = useState<Stop[]>([])
  const [extraCharge, setExtraCharge] = useState(0)
  const [name, setName] = useState(member.name)
  const [phone, setPhone] = useState(member.phone)
  const [count, setCount] = useState(1)
  const [luggage, setLuggage] = useState(0)
  const [nameBoard, setNameBoard] = useState(false)
  const [childSeat, setChildSeat] = useState(0)
  const [boosterSeat, setBoosterSeat] = useState(0)
  const [notes, setNotes] = useState('')
  const [quote, setQuote] = useState<{ total: number | null; addons: { label: string; amount: number }[] } | null>(null)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState('')

  useEffect(() => {
    if (isTrip) return
    setRegion('')
    api.get('/member/me/regions', { ...auth, params: { airport } }).then(r => setRegions(r.data.regions)).catch(() => setRegions([]))
  }, [airport, auth, isTrip])

  useEffect(() => {
    if (isTrip || !region) { setQuote(null); return }
    api.get('/member/me/quote', {
      ...auth,
      params: { airport, region, vehicle_type: vehicle || undefined, category, extra_charge: stops.length ? extraCharge : 0, name_board: nameBoard, child_seat_count: childSeat, booster_seat_count: boosterSeat },
    }).then(r => setQuote(r.data)).catch(() => setQuote(null))
  }, [airport, region, vehicle, category, nameBoard, childSeat, boosterSeat, stops.length, extraCharge, auth, isTrip])

  const submit = async () => {
    setErr('')
    if (!date || !time) { setErr('請選擇日期與時間'); return }
    if (isTrip) {
      if (!tripFrom.trim() || !tripTo.trim()) { setErr('請輸入上車與下車地址'); return }
    } else if (!address.trim()) { setErr(category === '送機' ? '請輸入上車地址' : '請輸入下車地址'); return }
    setSaving(true)
    try {
      const { data } = await api.post('/member/me/orders', {
        order_category: category, passenger_name: name, passenger_phone: phone, passenger_count: count, luggage_count: luggage,
        pickup_datetime: `${date}T${time}:00`,
        pickup_location: isTrip ? tripFrom : category === '送機' ? address : airport,
        dropoff_location: isTrip ? tripTo : category === '送機' ? airport : address,
        additional_stops: toStopPayload(stops), extra_charge: stops.length ? extraCharge : 0,
        vehicle_type: vehicle || undefined, flight_number: isTrip ? undefined : flight || undefined,
        departure_time: !isTrip && category === '送機' ? flightTime || undefined : undefined,
        arrival_time: !isTrip && category === '接機' ? flightTime || undefined : undefined, driver_notes: notes || undefined,
        airport: isTrip ? undefined : airport, region: isTrip ? undefined : region || undefined, name_board: nameBoard, child_seat: childSeat, booster_seat: boosterSeat,
      }, auth)
      onNotice({ ok: true, text: `訂單已成立（${data.order_number}）` }); onCreated()
    } catch (e: any) {
      if (e?.response?.status === 401) onUnauthorized()
      else setErr(errMsg(e, '下單失敗，請稍後再試'))
    } finally { setSaving(false) }
  }

  return (
    <div className="bg-white rounded-xl p-4 space-y-3">
      <TabRow options={CATEGORIES} value={category} onChange={setCategory} />
      {!isTrip && <TabRow options={AIRPORTS.map(a => ({ value: a, label: a }))} value={airport} onChange={setAirport} />}
      {info.vehicle_categories.length > 0 && (
        <TabRow options={info.vehicle_categories.map(v => ({ value: v, label: v }))} value={vehicle} onChange={setVehicle} />
      )}
      {isTrip ? (
        <>
          <WheelDateTimeField date={date} time={time} onChange={(d, t) => { setDate(d); setTime(t) }} />
          <AddressAutocomplete value={tripFrom} onChange={setTripFrom} placeholder="上車地址" />
          <StopsEditor stops={stops} onChange={setStops} extraCharge={extraCharge} onExtraChargeChange={setExtraCharge} />
          <AddressAutocomplete value={tripTo} onChange={setTripTo} placeholder="下車地址" />
        </>
      ) : (
        <>
          <div className="grid grid-cols-[1.35fr_1fr_1fr] gap-1.5">
            <WheelDateTimeField compact date={date} time={time} onChange={(d, t) => { setDate(d); setTime(t) }} />
            <select className={`${inputCls} !px-2`} value={region} onChange={e => setRegion(e.target.value)}>
              <option value="">{category === '送機' ? '上車區域' : '下車區域'}</option>
              {regions.map(r => <option key={r}>{r}</option>)}
            </select>
            <input className={`${inputCls} !px-2`} placeholder="航班編號" value={flight} onChange={e => setFlight(e.target.value)} />
          </div>
          {category === '送機' ? (
            <>
              <AddressAutocomplete value={address} onChange={setAddress} placeholder="上車地址" />
              <StopsEditor stops={stops} onChange={setStops} extraCharge={extraCharge} onExtraChargeChange={setExtraCharge} />
            </>
          ) : (
            <>
              <StopsEditor stops={stops} onChange={setStops} extraCharge={extraCharge} onExtraChargeChange={setExtraCharge} />
              <AddressAutocomplete value={address} onChange={setAddress} placeholder="下車地址" />
            </>
          )}
          <WheelDateTimeField timeOnly date="" time={flightTime} onChange={(_, t) => setFlightTime(t)} placeholder={category === '接機' ? '航班抵達時間（選填）' : '航班起飛時間（選填）'} />
        </>
      )}
      <div className="grid grid-cols-2 gap-2">
        <input className={inputCls} placeholder="乘客姓名" value={name} onChange={e => setName(e.target.value)} />
        <input className={inputCls} placeholder="聯絡電話" inputMode="tel" value={phone} onChange={e => setPhone(e.target.value)} />
      </div>
      <div className="grid grid-cols-4 gap-1.5 text-xs text-gray-600">
        <label>人數<input type="number" min={1} className={`${inputCls} !px-2`} value={count} onChange={e => setCount(parseInt(e.target.value) || 1)} /></label>
        <label>行李數<input type="number" min={0} className={`${inputCls} !px-2`} value={luggage} onChange={e => setLuggage(Math.max(parseInt(e.target.value) || 0, 0))} /></label>
        <label>兒童座椅<input type="number" min={0} className={`${inputCls} !px-2`} value={childSeat} onChange={e => setChildSeat(Math.max(parseInt(e.target.value) || 0, 0))} /></label>
        <label>增高墊<input type="number" min={0} className={`${inputCls} !px-2`} value={boosterSeat} onChange={e => setBoosterSeat(Math.max(parseInt(e.target.value) || 0, 0))} /></label>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-sm text-gray-700">舉牌</span>
        <button type="button" role="switch" aria-checked={nameBoard} onClick={() => setNameBoard(v => !v)}
          className={`relative w-11 h-6 rounded-full cursor-pointer transition-colors ${nameBoard ? 'bg-[#7f802e]' : 'bg-gray-300'}`}>
          <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${nameBoard ? 'translate-x-5' : ''}`} />
        </button>
      </div>
      <textarea className={inputCls} rows={2} placeholder="備註（選填）" value={notes} onChange={e => setNotes(e.target.value)} />
      {stops.length > 0 && extraCharge > 0 && <div className="text-sm text-gray-700">加價: {extraCharge.toLocaleString()}</div>}
      {quote?.total != null && (
        <div className="rounded-lg bg-[#f1f1dc] px-3 py-2 text-sm text-gray-900">
          預估車資 <b>${quote.total.toLocaleString()}</b>
          {quote.addons.length > 0 && <span className="text-xs text-[#6b6c26]">（含 {quote.addons.map(a => a.label).join('、')}）</span>}
        </div>
      )}
      {isTrip && <p className="text-xs text-gray-500">交通趟車資依路程計算，由車行確認。</p>}
      <p className="text-xs text-gray-400">送出後訂單即成立，上車前 {info.edit_hours} 小時以前都可自行修改或取消。</p>
      {err && <p className="text-xs text-red-600">{err}</p>}
      <button className={`${btnPrimary} w-full`} disabled={saving} onClick={submit}>{saving ? '送出中…' : '確認下單'}</button>
    </div>
  )
}
